/**
 * `@dxf-ai-editor/cad-core` — typed render-math (DEFUSE plan S10b).
 *
 * The canonical package for typed bounds / grips / select geometry computed
 * directly from the `DrawingEntity` model, with NO dependency on
 * `EntityPreview.properties` string bag. This finalises the Phase-0 stub
 * (S0.1) — three public functions (`getEntityBounds`, `getEntityGrips`,
 * `getSelectGeometry`) covering every kind in `drawingEntitySchema`'s
 * discriminated union (Line, Point, Circle, Arc, Ellipse, Spline,
 * LWPOLYLINE, Text, MText, Dimension, Multileader, Revision Cloud,
 * Wipeout, Hatch, Block, Rectangular Associative Array, Viewport).
 *
 * Import-allowlist (enforced by `.dependency-cruiser.cjs`
 * `cad-core-no-back-edges`):
 *   - `cad-core → drawing-model`   OK (canonical model dep; this package
 *     also REUSES drawing-model's existing one-truth geometry primitives —
 *     `bulgeArc`, `splineMath`, `hatchBoundaryGeometry`,
 *     `viewportMembership` — rather than re-deriving them, so the two
 *     typed geometry consumers can never drift).
 *   - `scene3d` / `scene` → `cad-core`   OK (forward edge; NOT wired in
 *     this slice — see the module docs below for the explicit 0-importers
 *     / foundation-only scope note).
 *   - `command-engine → cad-core`  OK (S15 extent, future).
 *   - reverse edges (`cad-core → apps/web` / `cad-core → command-engine`)
 *     are FORBIDDEN — cad-core stays a neutral leaf package.
 *
 * Structure: per-concern modules (`bounds.ts` / `grips.ts` / `select.ts`),
 * each dispatching over every entity kind, plus shared geometry primitives
 * (`common.ts`) and the Block/INSERT transform-chain resolver
 * (`blockGeometry.ts`, reusing `drawing-model/viewportMembership.ts`).
 * This barrel re-exports ONLY the public surface below — never per-kind
 * internals — so the barrel does not grow as kinds are added (file-size
 * SLA, CLAUDE.md "Контроль размера файлов").
 *
 * FOUNDATION-ONLY SLICE (category B, per the task's classification): this
 * PR has ZERO importers outside its own test suite — it does not wire into
 * any live UI / render / selection path. Wiring `getEntityBounds` /
 * `getEntityGrips` / `getSelectGeometry` into the live bounds / grips /
 * window-select code (`apps/web/src/app/scene/entityBounds.ts` +
 * `gripCalculation.ts` + `scene3d/grips/computeGrips.ts`) is a SEPARATE,
 * later Wave-2 slice with its own visual-verification gate (category C —
 * it changes what the renderer shows).
 */

import type { BlockInsertEntity, DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { getEntityBounds as getEntityBoundsImpl } from "./bounds";
import { getEntityGrips as getEntityGripsImpl } from "./grips";
import { getSelectGeometry as getSelectGeometryImpl } from "./select";
import {
  resolveBlockInsertBoundsOrNull as resolveBlockInsertBoundsOrNullImpl,
  type BlockLookupContext,
} from "./blockGeometry";

export type {
  BlockDefinitionBoundsMemo,
  BlockLookupContext,
  DimensionStyleTable,
  DynamicChildrenResolver,
  DynamicInstanceChildren,
} from "./blockGeometry";
export type {
  EntityBounds,
  GripKind,
  GripPatchTarget,
  GripPoint,
  SelectGeometry,
  SelectSegment,
} from "./types";
export type { Vec2 } from "./common";
// N2 (S10E-ANNOTATIVE-STUB-REPROJECT) — `unionBounds` is generic AABB-merge
// plumbing (no CAD-kind dispatch, unlike `getEntityBounds`/`getEntityGrips`/
// `getSelectGeometry`), so exposing it does not pull the "wiring is a
// SEPARATE Wave-2 slice" scope note above along with it: this is the FIRST
// live production caller (`scene3d/state/spatialBuilder.ts`'s annotative
// Block/INSERT stub reprojection unions `blockFromEntity`'s flattened
// children bounds), reusing the one existing implementation instead of a
// second copy (jscpd flagged the copy as a semantic duplicate — CLAUDE.md
// ПРИОРИТЕТ #1 default is to eliminate the clone via reuse).
export { unionBounds, unitVec2 } from "./common";
// Codex PR #784 round 7 P2 — the block-reference LINEAR algebra and the
// recursion PLACEHOLDER geometry are shared, one-truth surfaces: the renderer
// (`scene3d/adapters/typed/blockFromEntity.ts`, `blockChildMultileader.ts`)
// composes nested references with the SAME exact `R·S·R` rule the bounds walk
// replays to bound what it draws, and draws the placeholder from the SAME
// size / label formulas `blockDefinitionWalk.ts` reserves a box for. cad-core
// cannot import from `apps/web` (`cad-core-no-back-edges`), so the math lives
// here and the renderer consumes it — never a second copy.
// `blockLinearMatrix` / `applyMat2` / `Mat2` join that surface for Codex PR
// #784 round 11 P2: an arrowhead GLYPH inside a non-uniformly scaled INSERT is
// not a "direction + one size" similarity — the enclosing 2×2 stretches its
// along-direction and its perpendicular by DIFFERENT factors and shears a
// diagonal one — so the render arm hands the mesh builder the full 2×2 map
// built from THIS module's `R·S·R` (never a second derivation of it). The
// BOUNDS side needs no change for it: `leafBounds.ts::multileaderArrowCorners`
// reserves the arrow footprint in the MLEADER's own local frame and the block
// walk pushes that box through the very same INSERT affine, whose image
// therefore contains every glyph corner the renderer now draws.
export {
  applyMat2,
  blockLinearMatrix,
  composeBlockLinearExact,
  composeNestedBlockLinear,
  decomposeBlockLinear,
  guardBlockScale,
  IDENTITY_BLOCK_LINEAR,
  type BlockLinearRsr,
  type BlockLinearTriple,
  type Mat2,
} from "./blockLinearTransform";
export {
  MAX_BLOCK_RECURSION_DEPTH,
  recursionPlaceholderLabelEntity,
  recursionPlaceholderSquareCorners,
  recursionPlaceholderSquareSize,
} from "./recursionPlaceholder";
// Codex PR #785 round 1, F3 — the ONE-TRUTH wire topology of a heavy POLYLINE
// mesh (M×N rows/columns, indexed face edges), consumed by this package's own
// select geometry AND — the reason it is EXPORTED rather than module-private —
// by the render adapter that draws the same wires
// (`apps/web/.../adapters/typed/polylineFromEntity.ts`, whose private
// `polygonMeshRuns` / `polyfaceRuns` this replaces). `apps/web` already depends
// on this package, so that is the app→package convergence direction the repo's
// other shared-truth helpers use; the runs carry INDICES, not coordinates, so a
// `Vec3`-building renderer and a `Vec2`-picking selector share them verbatim.
export {
  polylineLocationRecordIndices,
  polylineMeshWireRuns,
  type MeshWireRun,
  type PolylineMeshTopologySource,
} from "./polylineMeshTopology";

// The ONE-truth DRAWABLE topology of a heavy POLYLINE of ANY subtype — which
// records are visible geometry (the spline-fit frame is NOT), in what order,
// which spans are curved, and where the run lands in WCS (the 2D OCS lift).
// The union of the mesh runs above with the 2D/3D vertex run, consumed by the
// render adapter, the picker, the vector-PDF plot and the AI `osnap-query`
// route so the canvas, the pick, the plot and the agent-facing snap can never
// disagree about the shape or the PLACE of the same entity (@codex PR #785
// round 3, F1/F2/F3; round 4, F1/F2/F3 — the picker and the AI route were still
// testing / reporting raw OCS coordinates, and the AI route dropped meshes).
export {
  heavyPolylineRuns,
  heavyPolylineVisibleIndices,
  heavyPolylineWcsResolver,
  type HeavyPolylineGeometrySource,
  type HeavyPolylineRecordFlags,
  type HeavyPolylineRun,
  type HeavyPolylineWcsPoint,
} from "./heavyPolylineTopology";

// The WIDE-POLYLINE BAND pipeline — the ONE span walk (`polylineSpanSubSegments`),
// the ONE drawn centreline (`polylineCentrelineSamples`: project into the
// entity's own plane, walk the spans there, lift the samples) and the ONE drawn
// silhouette (`widePolylineBandLoops`), plus the offset math and the frame pair
// they compose. Relocated here from `apps/web/.../scene3d/adapters/geom/` (which
// re-exports every symbol, so no import path changed) when THIS package's heavy-
// POLYLINE select geometry became a consumer: window / crossing selection must
// test the same drawn curve and the same drawn band the canvas strokes, the
// snaps measure and the vector plot fills, and `cad-core` may not import
// `apps/*` (`.dependency-cruiser.cjs::cad-core-no-back-edges`). Hoisting into
// the package both sides already depend on keeps ONE truth; a copy on this side
// would have been a second one (@codex PR #785 round 8).
export {
  linearPolylinePlane,
  polylinePlane,
  type PolylineBandFrame,
  type PolylinePlane,
} from "./polylinePlane";
export {
  allWidthsZero,
  COINCIDENT_EPS,
  dropDegenerateSegments,
  MITER_LIMIT,
  widePolylineOutlineLoops,
  type WidthSegment,
} from "./polylineWidthOutline";
export {
  BAND_ARC_SAMPLES_PER_TURN,
  polylineBandSegments,
  polylineCentrelineSamples,
  polylineSpanSubSegments,
  widePolylineBandBounds,
  widePolylineBandLoops,
  type PolylineSubSegment,
} from "./polylineWidthBand";

// The BOX of that drawn silhouette, swept along the group-39 thickness — the
// ONE function `./bounds.ts`' `"Polyline"` arm, `./blockDefinitionWalk.ts`'
// block-child arm and `apps/web`'s `state/entityMetadataIndex.ts` all answer
// "where is this heavy POLYLINE?" with. Surfaced on the barrel in round 10:
// the cache-direct metadata index was still on width-blind
// `computeEntityWcsBounds`, so a wide polyline was indexed at its CENTRELINE
// and `lod/visibleSet.ts computeVisibleSet` — which starts at
// `spatial.intersect(viewport)` and never requests a lift for an id the rbush
// did not return — dropped a ribbon the canvas plainly draws (@codex PR #785
// round 10).
export { heavyPolylineDrawnBounds } from "./heavyPolylineDrawnBounds";

// The same answer for an LWPOLYLINE, which carries the same authored widths
// (the group-43 constant, or the per-vertex 40 / 41 pair that supersedes it)
// and is drawn as the same mitred band: the ONE function `./bounds.ts`'
// `"LWPOLYLINE"` arm and `./blockDefinitionWalk.ts`' block-child arm both
// answer with, and therefore what `apps/web`'s `state/entityMetadataIndex.ts`
// publishes to the rbush on the cache-direct path. Round 10 closed the heavy
// POLYLINE and left this one a DECLARED gap; round 11's F1 closes it
// (@codex PR #785).
export { lwpolylineDrawnBounds } from "./lwpolylineDrawnBounds";

/**
 * Typed axis-aligned bounds of an entity, read directly from the canonical
 * `DrawingEntity` (NOT the `EntityPreview.properties` string bag).
 *
 * `ctx.blockDefinitions` (optional) resolves a `Block` (INSERT) entity's
 * REAL footprint — the referenced block definition's child geometry,
 * transformed by the insert (base-point subtract → scale → rotate →
 * translate, tiled across a MINSERT grid when present). Without it, a
 * `Block` returns its insertion-point-only degenerate box (an honest
 * "extent unknown beyond the placement point", never a fabricated
 * icon-sized box).
 *
 * Returns `null` when the entity carries no usable positional geometry
 * (mirrors `entityBounds.ts`'s `null` for a degenerate / non-finite shape).
 */
export function getEntityBounds(
  entity: DrawingEntity,
  ctx?: BlockLookupContext,
) {
  return getEntityBoundsImpl(entity, ctx);
}

/**
 * Typed grip points of an entity, read directly from the canonical
 * `DrawingEntity`. See `grips.ts`'s module doc for the documented `Block`
 * (INSERT) grip-set scope note (base insertion grip only — dynamic-block /
 * MINSERT parameter grips are a separate, larger, explicitly-deferred
 * subsystem).
 */
export function getEntityGrips(entity: DrawingEntity) {
  return getEntityGripsImpl(entity);
}

/**
 * Typed window / crossing select geometry of an entity — the entity's
 * bounds plus the (possibly tessellated-curve) points/segments a selection
 * test rasterises against. `ctx` is threaded through to `getEntityBounds`
 * for the `Block` kind. Returns `null` when the entity has no usable bounds.
 */
export function getSelectGeometry(
  entity: DrawingEntity,
  ctx?: BlockLookupContext,
) {
  return getSelectGeometryImpl(entity, ctx);
}

/**
 * `Block` (INSERT)-specific sibling of `getEntityBounds`'s `Block` dispatch
 * case: resolves the SAME real transformed footprint, but returns `null`
 * instead of the insertion-point-only fallback when the referenced
 * definition genuinely has no usable child geometry (missing / cyclic /
 * every child unsupported or positionless) or no `ctx.blockDefinitions` was
 * supplied at all.
 *
 * Every OTHER `getEntityBounds` kind already returns `null` for "no usable
 * positional geometry" (see that function's own doc). The `Block` case
 * alone cannot: the generic dispatcher must always hand render/select/grips
 * consumers SOME box (never `null` for a Block — an honest "extent unknown
 * beyond the placement point"), which collapses "genuinely unresolved" and
 * a legitimate degenerate `{0,0,0,0}` transformed AABB (e.g. a lone
 * Point/Text/Attdef child sitting exactly at the block's local origin) into
 * the SAME value. A caller that already holds (or synthesizes) a
 * `BlockInsertEntity` directly and needs to tell the two apart — e.g.
 * `computeBlockBounds`'s overlap-warning footprint (codex PR #472 finding
 * 2) — calls this instead of the generic `getEntityBounds` dispatcher.
 */
export function getBlockInsertBoundsOrNull(
  entity: BlockInsertEntity,
  ctx?: BlockLookupContext,
) {
  return resolveBlockInsertBoundsOrNullImpl(entity, ctx);
}
